import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { TrendingUp, Users, Smile, BarChart3, ChevronDown } from 'lucide-react';
import AppContext from '../../../../context/AppContext'; // apna actual path yahan set kr lena
import '../../../../assests/css/admin/AiFeature.css';

const AiFeature = () => {
  const navigate = useNavigate();
  const { baseUrl } = useContext(AppContext);

  const [isVisible, setIsVisible] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState('Last 7 Days');
  const [loading, setLoading] = useState(true);

  const [statsData, setStatsData] = useState({ totalTryOns: 0, totalCustomizations: 0, satisfaction: 0 });
  const [chartData, setChartData] = useState([]);
  const [sentiment, setSentiment] = useState({ positive: 0, neutral: 0, negative: 0 });
  const [feedbackData, setFeedbackData] = useState([]);
  const [toolPerformance, setToolPerformance] = useState([]);

  const fetchAnalytics = () => {
    setLoading(true);

    // ── FIX: pehle yahan axios.post(...) ke baad galti se comma (,)
    // laga hua tha jo .then() chain ko tod raha tha, aur "period"
    // naam ka koi variable exist nahi karta tha (selectedFilter
    // hona chahiye tha). Route bhi backend ke actual registered
    // route (/admin/aifeature/analytics) se match karwa diya hai.
    axios
      .post(`${baseUrl}/admin/aifeature/analytics`, { period: selectedFilter })
      .then((res) => {
        if (res.data.success) {
          setStatsData(res.data.stats);
          setChartData(res.data.chartData);
          setSentiment(res.data.sentiment);
          setFeedbackData(res.data.feedbackData);
          setToolPerformance(res.data.toolPerformance);
        }
        setLoading(false);
        setTimeout(() => setIsVisible(true), 100);
      })
      .catch((error) => {
        console.log("Error fetching AI feature analytics:", error);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilter]);

  const statsCards = [
    {
      icon: <Users size={24} />,
      label: 'Total AI Try-ons',
      value: statsData.totalTryOns.toLocaleString(),
      color: 'blue'
    },
    {
      icon: <BarChart3 size={24} />,
      label: 'Total AI Customizations',
      value: statsData.totalCustomizations.toLocaleString(),
      color: 'purple'
    },
    {
      icon: <Smile size={24} />,
      label: 'Overall Satisfaction',
      value: `${statsData.satisfaction}%`,
      color: 'orange'
    }
  ];

  const filterOptions = ['Last 7 Days', 'Last 30 Days', 'Last 3 Months', 'Last Year'];

  const handleAnalyzeFeedback = () => {
    navigate('/admin/feedback');
  };

  const handleViewAllReviews = (e) => {
    e.preventDefault();
    navigate('/admin/feedback');
  };

  const handleViewDetailedReport = () => {
    navigate('/admin/analytics');
  };

  if (loading) {
    return <div className="AdminPanel-AiFeature-container">Loading...</div>;
  }

  return (
    <div className="AdminPanel-AiFeature-container">
      <div className="AdminPanel-AiFeature-header">
        <h1 className="AdminPanel-AiFeature-title">AI Features Dashboard</h1>
        <div className="AdminPanel-AiFeature-dropdown">
          <button 
            className="AdminPanel-AiFeature-dropdown-button"
            onClick={() => setDropdownOpen(!dropdownOpen)}
          >
            {selectedFilter}
            <ChevronDown size={20} className={`AdminPanel-AiFeature-dropdown-icon ${dropdownOpen ? 'open' : ''}`} />
          </button>
          {dropdownOpen && (
            <div className="AdminPanel-AiFeature-dropdown-menu">
              {filterOptions.map((option) => (
                <div
                  key={option}
                  className="AdminPanel-AiFeature-dropdown-item"
                  onClick={() => {
                    setSelectedFilter(option);
                    setDropdownOpen(false);
                  }}
                >
                  {option}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className={`AdminPanel-AiFeature-stats-grid ${isVisible ? 'visible' : ''}`}>
        {statsCards.map((card, index) => (
          <div 
            key={index} 
            className="AdminPanel-AiFeature-stat-card"
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            <div className={`AdminPanel-AiFeature-stat-icon ${card.color}`}>
              {card.icon}
            </div>
            <div className="AdminPanel-AiFeature-stat-content">
              <p className="AdminPanel-AiFeature-stat-label">{card.label}</p>
              <h2 className="AdminPanel-AiFeature-stat-value">{card.value}</h2>
            </div>
          </div>
        ))}
      </div>

      <div className="AdminPanel-AiFeature-content-grid">
        <div className={`AdminPanel-AiFeature-chart-card ${isVisible ? 'visible' : ''}`}>
          <div className="AdminPanel-AiFeature-chart-header">
            <h3>Feature Usage Trends</h3>
            <div className="AdminPanel-AiFeature-legend">
              <span className="AdminPanel-AiFeature-legend-item">
                <span className="AdminPanel-AiFeature-legend-dot blue"></span>
                Try-on
              </span>
              <span className="AdminPanel-AiFeature-legend-item">
                <span className="AdminPanel-AiFeature-legend-dot purple"></span>
                Customization
              </span>
            </div>
          </div>
          <div className="AdminPanel-AiFeature-chart">
            {chartData.map((data, index) => {
              const maxVal = Math.max(...chartData.map(d => Math.max(d.tryOn, d.customization)), 1);
              const maxHeight = 250;
              const tryOnHeight = (data.tryOn / maxVal) * maxHeight;
              const customHeight = (data.customization / maxVal) * maxHeight;

              return (
                <div key={index} className="AdminPanel-AiFeature-chart-bar-group">
                  <div className="AdminPanel-AiFeature-chart-bars">
                    <div 
                      className="AdminPanel-AiFeature-chart-bar blue"
                      style={{ 
                        height: `${tryOnHeight}px`,
                        animationDelay: `${0.5 + index * 0.1}s`
                      }}
                    ></div>
                    <div 
                      className="AdminPanel-AiFeature-chart-bar purple"
                      style={{ 
                        height: `${customHeight}px`,
                        animationDelay: `${0.6 + index * 0.1}s`
                      }}
                    ></div>
                  </div>
                  <span className="AdminPanel-AiFeature-chart-label">{data.day}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className={`AdminPanel-AiFeature-sentiment-card ${isVisible ? 'visible' : ''}`}>
          <h3>Sentiment Analysis</h3>
          <p className="AdminPanel-AiFeature-sentiment-subtitle">Customer feedback tone</p>
          <div className="AdminPanel-AiFeature-sentiment-circle">
            <svg viewBox="0 0 200 200">
              <defs>
                <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#10b981" />
                  <stop offset="100%" stopColor="#059669" />
                </linearGradient>
              </defs>
              <circle cx="100" cy="100" r="80" className="AdminPanel-AiFeature-circle-bg" />
              <circle 
                cx="100" 
                cy="100" 
                r="80" 
                className="AdminPanel-AiFeature-circle-progress"
                stroke="url(#progressGradient)"
              />
            </svg>
            <div className="AdminPanel-AiFeature-sentiment-text">
              <span className="AdminPanel-AiFeature-sentiment-value">{sentiment.positive}%</span>
              <span className="AdminPanel-AiFeature-sentiment-label">POSITIVE</span>
            </div>
          </div>
          <div className="AdminPanel-AiFeature-sentiment-breakdown">
            <div className="AdminPanel-AiFeature-sentiment-item">
              <span className="AdminPanel-AiFeature-sentiment-dot satisfied"></span>
              <span className="AdminPanel-AiFeature-sentiment-name">Satisfied</span>
              <span className="AdminPanel-AiFeature-sentiment-percent">{sentiment.positive}%</span>
            </div>
            <div className="AdminPanel-AiFeature-sentiment-item">
              <span className="AdminPanel-AiFeature-sentiment-dot neutral"></span>
              <span className="AdminPanel-AiFeature-sentiment-name">Neutral</span>
              <span className="AdminPanel-AiFeature-sentiment-percent">{sentiment.neutral}%</span>
            </div>
            <div className="AdminPanel-AiFeature-sentiment-item">
              <span className="AdminPanel-AiFeature-sentiment-dot negative"></span>
              <span className="AdminPanel-AiFeature-sentiment-name">Negative</span>
              <span className="AdminPanel-AiFeature-sentiment-percent">{sentiment.negative}%</span>
            </div>
          </div>
          <button 
            className="AdminPanel-AiFeature-analyze-button"
            onClick={handleAnalyzeFeedback}
          >
            Analyze Recent Feedback
          </button>
        </div>
      </div>

      <div className="AdminPanel-AiFeature-bottom-grid">
        <div className={`AdminPanel-AiFeature-feedback-card ${isVisible ? 'visible' : ''}`}>
          <div className="AdminPanel-AiFeature-card-header">
            <div>
              <h3>Customer Feedback</h3>
              <span className="AdminPanel-AiFeature-live-badge">LIVE</span>
            </div>
            <a 
              href="#" 
              className="AdminPanel-AiFeature-view-all"
              onClick={handleViewAllReviews}
            >
              View All Reviews
            </a>
          </div>
          <div className="AdminPanel-AiFeature-feedback-list">
            {feedbackData.length === 0 && (
              <p style={{ textAlign: 'center', padding: '20px' }}>No feedback yet.</p>
            )}
            {feedbackData.map((feedback, index) => (
              <div key={index} className="AdminPanel-AiFeature-feedback-item">
                <div 
                  className="AdminPanel-AiFeature-feedback-avatar"
                  style={{
                    background: feedback.color === 'blue' 
                      ? 'linear-gradient(135deg, #3b82f6, #2563eb)' 
                      : 'linear-gradient(135deg, #8b5cf6, #7c3aed)'
                  }}
                >
                  {feedback.initials}
                </div>
                <div className="AdminPanel-AiFeature-feedback-content">
                  <div className="AdminPanel-AiFeature-feedback-header-row">
                    <span className="AdminPanel-AiFeature-feedback-name">{feedback.name}</span>
                    <span className={`AdminPanel-AiFeature-feedback-badge ${feedback.color}`}>
                      {feedback.badge}
                    </span>
                  </div>
                  <div className="AdminPanel-AiFeature-feedback-stars">
                    {[...Array(5)].map((_, i) => (
                      <span 
                        key={i} 
                        className={`AdminPanel-AiFeature-star ${i < feedback.rating ? 'filled' : ''}`}
                      >
                        ★
                      </span>
                    ))}
                  </div>
                  <p className="AdminPanel-AiFeature-feedback-comment">{feedback.comment}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className={`AdminPanel-AiFeature-performance-card ${isVisible ? 'visible' : ''}`}>
          <div className="AdminPanel-AiFeature-card-header">
            <h3>Tool Performance</h3>
            <button 
              className="AdminPanel-AiFeature-detail-button"
              onClick={handleViewDetailedReport}
            >
              View Detailed Report
            </button>
          </div>
          <div className="AdminPanel-AiFeature-tool-list">
            {toolPerformance.map((tool, index) => (
              <div key={index} className="AdminPanel-AiFeature-tool-item">
                <div className="AdminPanel-AiFeature-tool-info">
                  <span className="AdminPanel-AiFeature-tool-name">{tool.name}</span>
                  <span className="AdminPanel-AiFeature-tool-rating">
                    {tool.rating} <span className="AdminPanel-AiFeature-rating-max">/5</span>
                  </span>
                </div>
                <div className="AdminPanel-AiFeature-progress-bar">
                  <div 
                    className="AdminPanel-AiFeature-progress-fill"
                    style={{ 
                      width: `${tool.percentage}%`,
                      animationDelay: `${1 + index * 0.2}s`
                    }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AiFeature;